import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { BomDto, BomItemDto, CostCentreDto, PartResponse } from 'app/api/models';
import { BomService, CostCentreService, PartService } from 'app/api/services';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { getParentUrl } from 'app/app.component';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule, AsyncPipe, I18nPluralPipe, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRippleModule, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FuseFindByKeyPipe } from '@fuse/pipes/find-by-key';
import { SharedComponentsModule } from 'app/core/components/shared-components.module';
import { SharedModule } from 'app/core/shared.module';
import { DLMSharedComponentsModule } from 'app/views/calibrations/shared/components/shared-components.module';
import { DLMSharedModule } from 'app/views/calibrations/shared/shared.module';

@Component({
  selector: 'app-bom-details-popup',
  templateUrl: './bom-details-popup.component.html',
  styleUrls: ['./bom-details-popup.component.scss'],
  standalone: true,
  imports: [
        CommonModule,
        SharedModule,
        RouterOutlet,
        MatSidenavModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        RouterLink,
        AsyncPipe,
        I18nPluralPipe,
        MatTooltipModule,
        MatRippleModule,
        MatCheckboxModule,
        MatSelectModule,
        MatOptionModule,
        MatDatepickerModule,
        TextFieldModule,
        FuseFindByKeyPipe,
        DatePipe,
        MatSlideToggleModule,
        MatAutocompleteModule,
        MatPaginatorModule,
        MatBadgeModule,
        SharedComponentsModule,
        MatMenuModule,
        MatDialogModule,

        CommonModule,
        DLMSharedModule,
        DLMSharedComponentsModule,
  ],
})
export class BomDetailsPopupComponent implements OnInit {
  public bom: BomDto;
  public bomItems: BomItemDto[];
  public bomAssemblyProcesses: any[];
  public parts: PartResponse[] = [];

  // Operation codes and cost centers (same as bom-builder)
  public operationCodes = [
    { code: 'SUBCO', name: 'Sub-Contract' },
    { code: 'INSPE', name: 'Inspection' },
    { code: 'KITTI', name: 'Kitting' },
    { code: 'GAUGE', name: 'Gauge' },
    { code: 'OVEN', name: 'Oven' },
    { code: 'POSTC', name: 'Post Cure' },
    { code: 'WIRE', name: 'Wire' },
    { code: 'ASSEM', name: 'Assembly' },
    { code: 'PROOF', name: 'Proof Load' },
    { code: 'CALIB', name: 'Calibration' },
    { code: 'FINAL', name: 'Final Inspection' },
    { code: 'PACKI', name: 'Packing' },
    { code: 'MACHI', name: 'Machining' },
  ];

  public costCentres: CostCentreDto[] = [
    { code: '100TC', name: '100t Calibration Machine', rate: 0.00 },
    { code: '18CAL', name: '18T Calibration Machine', rate: 0.00 },
    { code: 'MACHI', name: 'Machining', rate: 0.00 },
    { code: 'ASSEM', name: 'Assembly', rate: 0.00 },
    { code: 'WIRE', name: 'Wire', rate: 0.00 },
    { code: 'PROOF', name: 'Proof Load', rate: 0.00 },
    { code: '60CAL', name: '60t Calibration', rate: 0.00 },
    { code: 'FINALINSP', name: 'Final Inspection', rate: 0.00 },
    { code: 'PACKI', name: 'Packing', rate: 0.00 },
    { code: 'SUBCO', name: 'Sub-Contract', rate: 0.00 },
    { code: 'INSPE', name: 'Inspection', rate: 0.00 },
    { code: 'KITTI', name: 'Kitting', rate: 0.00 },
    { code: 'GAUGE', name: 'Gauge', rate: 0.00 },
    { code: 'OVEN', name: 'Oven', rate: 0.00 },
    { code: 'POSTC', name: 'Post Cure', rate: 0.00 },
    { code: 'PAINTI', name: 'Painting', rate: 0.00 },
    { code: 'WELD', name: 'Welding', rate: 0.00 },
    { code: 'HEAT', name: 'Heat Treatment', rate: 0.00 },
    { code: 'SURFACET', name: 'Surface Treatment', rate: 0.00 },
    { code: 'QUALIT', name: 'Quality Control', rate: 0.00 },
    { code: 'LOGIS', name: 'Logistics', rate: 0.00 },
    { code: 'STORE', name: 'Storage', rate: 0.00 },
    { code: 'MAINT', name: 'Maintenance', rate: 0.00 },
    { code: 'ADMIN', name: 'Administration', rate: 0.00 },
    { code: 'R&D', name: 'Research & Development', rate: 0.00 },
    { code: 'ENGIN', name: 'Engineering', rate: 0.00 },
    { code: 'SALES', name: 'Sales & Marketing', rate: 0.00 },
    { code: 'PURCHAS', name: 'Purchasing', rate: 0.00 },
    { code: 'FINANC', name: 'Finance', rate: 0.00 },
    { code: 'HR', name: 'Human Resources', rate: 0.00 },
    { code: 'PROFI', name: 'Profiling', rate: 0.00 },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<BomDetailsPopupComponent>,
    private partService: PartService,
    private bomService: BomService,
    private costCentreService: CostCentreService,
    private router: Router,
    private datePipe: DatePipe,
  ) {}

  ngOnInit() {
    this.bom = this.data.bom || {};
    this.bomItems = this.data.bom?.items || [];
    this.bomAssemblyProcesses = this.data.bomAssemblyProcesses || [];

    // Load parts to resolve part details
    this.loadParts();
    this.loadCostCentres();
  }

  loadParts() {
    this.partService.apiPartPartsGet$Json({}).subscribe({
      next: (res) => {
        if (res?.data) {
          this.parts = res.data;
        }
      },
      error: (err) => {
        console.error('Error loading parts:', err);
      }
    });
  }

  loadCostCentres() {
    this.costCentreService.apiCostCentreCostCentresGet$Json$Response().subscribe((res) => {
      if (!res?.body?.data) return;

      this.costCentres = res.body.data;
    });
  }

  lookupPartFromId(partId: number): PartResponse | null {
    return this.parts?.find(x => x.id == partId) ?? null;
  }

  getCostCentreName(code: string): string {
    const costCentre = this.costCentres.find(cc => cc.code === code);
    return costCentre ? costCentre.name : code;
  }

  getProcessRate(process: any): number {
    if (process.isSubcontracted) {
      return process.subcontractorHourlyRate || 0;
    }
    return process.costCentreRate || 0;
  }

  getProcessTotal(process: any): number {
    const rate = this.getProcessRate(process);
    const totalHours = (process.setupHours || 0) * (process.isSubcontracted ? 35 : 1) + (process.runPerUnitHours || 0);
    return rate * totalHours;
  }

  getCostCentreProcesses() {
    return this.bomAssemblyProcesses.filter(p => !p.isSubcontracted);
  }

  getSubcontractorProcesses() {
    return this.bomAssemblyProcesses.filter(p => p.isSubcontracted && p.subcontractorName);
  }

  getCostCentreProcessesTotal(): number {
    return this.getCostCentreProcesses().reduce((total, process) => {
      return total + this.getProcessTotal(process);
    }, 0);
  }

  getAssemblySubcontractorTotal(): number {
    return this.getSubcontractorProcesses().reduce((total, process) => {
      return total + this.getProcessTotal(process);
    }, 0);
  }

  getMaterialTotal(): number {
    return this.bomItems.reduce((total, item) => {
      const part = this.lookupPartFromId(item.partId);
      const cost = part?.cost || 0;
      return total + ((item.quantity || 0) * cost);
    }, 0);
  }

  getGrandTotal(): number {
    return this.getMaterialTotal() + this.getCostCentreProcessesTotal() + this.getAssemblySubcontractorTotal();
  }

  getStatusText() {
    if (this.bom.isPhantom)
      return 'Phantom';
    else if (!this.bom.isNotDraft)
      return 'Draft';
    else if (!this.bom.approvalDate)
      return 'Pending';
    else if (this.bom.approvalStatus == 0) // Approve
      return 'Approved';
    else if (this.bom.approvalStatus == 1) // Reject
      return 'Rejected';
    else if (this.bom.approvalStatus == 2) // RequestChanges
      return 'Changes Requested';
    else
      return 'None';
  }

  getStatusColor() {
    if (!this.bom.isNotDraft || this.bom.isPhantom)
      return 'gray';
    else if (!this.bom.approvalDate)
      return 'orange';
    else if (this.bom.approvalStatus == 0)
      return 'green';
    else if (this.bom.approvalStatus == 1 || this.bom.approvalStatus == 2)
      return 'red';
    else
      return 'gray';
  }

  editBom() {
    // Close dialog first
    this.dialogRef.close();

    // Navigate to BOM builder with bomId - using absolute link as popup not tied to navigation
    this.router.navigateByUrl('/product-register/bom-builder' + (this.bom.bomId ? `?bomId=${this.bom.bomId}` : ''));
  }

  downloadDrawings(){
    this.bomService.apiBomDownloadDrawingsZipBomIdGet({bomId: this.bom.bomId}).subscribe(res => {
      if(!res) return;

      console.log("got zip!");

      const blob = new Blob([res], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      link.download = `BOM-${this.datePipe.transform(this.bom.createdDate, 'yyyy')}-${this.bom.bomId.toString().padStart(3, '0')} - Drawing Pack.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
    });
  }
}
